/* Refer-a-friend — v3 Crush SOP §3.1 ("Refer 3 friends and your
 * bundle becomes free") + §3.3 (refer-a-friend is one of the six
 * enforced sell surfaces).
 *
 * Frontend lane only. The backend (apex-flow-labs repo) owns the
 * ledger — who referred whom, qualifying purchases, refund issuance.
 * Until that ships, codes are generated DETERMINISTICALLY from a
 * stable seed (Stripe session id, or the buyer's email when signed
 * in), so:
 *
 *   - the same buyer always sees the same code (no DB needed)
 *   - the backend can later regenerate the identical code from the
 *     same seed and backfill the ledger without breaking any code a
 *     buyer already shared
 *
 * Honesty rule (Doctrine 3): the UI never claims "your bundle IS
 * free" — it says refunds are issued when 3 referred purchases
 * complete, which is exactly what the backend will do. No fake
 * progress counters: until the ledger API exists we show the code +
 * the rules, not a made-up "1 of 3" tracker. */

export const REFERRAL_REWARD_THRESHOLD = 3;

/* Codes look like APEX-7K2M9Q — 6 chars from an unambiguous alphabet
 * (no 0/O, 1/I/L) so they survive being read aloud or hand-typed. */
const CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";
const CODE_LENGTH = 6;
const CODE_PREFIX = "APEX-";

/* FNV-1a 32-bit — tiny, stable, dependency-free. Not cryptographic,
 * and doesn't need to be: codes are attribution handles, not secrets.
 * The backend validates against its ledger before paying anything. */
function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function referralCodeFor(seed: string): string {
  const normalized = seed.trim().toLowerCase();
  /* Two rounds with different salts → 10 chars of entropy from the
   * 32-bit hashes; take CODE_LENGTH. Collisions across two buyers are
   * possible but harmless — the backend keys its ledger on
   * (code, seed) and disambiguates. */
  const h1 = fnv1a(`apex-ref:${normalized}`);
  const h2 = fnv1a(`apex-ref2:${normalized}`);
  let out = "";
  let acc = (BigInt(h1) << 32n) | BigInt(h2);
  for (let i = 0; i < CODE_LENGTH; i++) {
    out += CODE_ALPHABET[Number(acc % BigInt(CODE_ALPHABET.length))];
    acc /= BigInt(CODE_ALPHABET.length);
  }
  return CODE_PREFIX + out;
}

export function isValidReferralCodeFormat(code: string): boolean {
  if (typeof code !== "string") return false;
  const trimmed = code.trim().toUpperCase();
  if (!trimmed.startsWith(CODE_PREFIX)) return false;
  const body = trimmed.slice(CODE_PREFIX.length);
  if (body.length !== CODE_LENGTH) return false;
  return [...body].every((c) => CODE_ALPHABET.includes(c));
}

export function referralShareUrl(code: string): string {
  const site =
    process.env.NEXT_PUBLIC_SITE_URL || "https://digital.apexflowlabs.com";
  return `${site.replace(/\/$/, "")}/browse-businesses?ref=${encodeURIComponent(code)}`;
}

/* ── Client-side capture (localStorage) ──────────────────────────────
 *
 * Visitor lands anywhere with ?ref=APEX-XXXXXX → code persists for
 * 30 days → both checkout routes read it at purchase time and stamp
 * Stripe metadata so the backend can attribute. */

const STORAGE_KEY = "ax-referral-v1";
const CAPTURE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

type StoredReferral = { code: string; capturedAt: number };

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

export function captureReferralCode(code: string): boolean {
  if (!isBrowser() || !isValidReferralCodeFormat(code)) return false;
  try {
    const payload: StoredReferral = {
      code: code.trim().toUpperCase(),
      capturedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

export function getStoredReferralCode(): string | null {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredReferral;
    if (!parsed?.code || !isValidReferralCodeFormat(parsed.code)) return null;
    if (Date.now() - parsed.capturedAt > CAPTURE_TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.code;
  } catch {
    return null;
  }
}

export function clearStoredReferralCode(): void {
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
